import { Box, Tooltip, useDisclosure } from '@chakra-ui/react';
import { IconSettingsSliderThree } from 'central-icons/IconSettingsSliderThree';
import { useCallback } from 'react';

import { Badge, BadgeColor } from '@revelio/ui';

import { useCustomDashPreferenceVisibility } from '../../../../shared/user-preferences/hooks/useCustomDashPreferenceVisibility';
import { PreferencesModal } from '../../../../shared/user-preferences/PreferencesModal';
import PreferencesTooltipContent from '../../../../shared/user-preferences/PreferenceTooltip';
import type { OnPreferenceUpdateCallback } from '../../../../shared/user-preferences/preference-editors/preferenceEditors';
import {
  PAGE_ORDER_SCOPES,
  PreferenceScope,
  UserPreferenceKey,
  type UserPreferenceSubKey,
  type UserPreferences,
} from '../../../../shared/user-preferences/types';
import { UserPreferencesDataProvider } from '../../../../shared/user-preferences/UserPreferencesDataProvider';
import { calculateVisiblePreferenceDiff } from '../../../../shared/user-preferences/utils/preference-diff';
import { filtersToPreferences } from '../../../../shared/user-preferences/utils/preference-filter-adapters';
import {
  getPreferenceScope,
  updateCustomDashFiltersForPreference,
} from '../../../../shared/user-preferences/utils/preferences';
import { getVisiblePreferenceKeys } from '../../../../shared/user-preferences/utils/visibility';
import { useFilterContext } from '../../filters/contexts/FilterProvider';
import { NO_PRIMARY_FILTER_KEY } from '../../filters/engine/filterGroupConsistency';
import {
  getPrimaryFilterIdForScreenerV2Page,
  isScreenerV2Page,
} from '../../utils/routing-helpers';
import { DataProviderQueryStatus } from '../DataProvider/types';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import { ActionBarIconButton } from './ActionBarIconButton';

export const UserPreferencesButtonBase = ({
  affectedScope,
  preferencesFromFilters,
  onPreferenceUpdate,
  preferenceVisibility,
  showSummary = false,
}: {
  affectedScope: PreferenceScope;
  preferencesFromFilters?: Partial<UserPreferences>;
  onPreferenceUpdate: OnPreferenceUpdateCallback;
  preferenceVisibility?: (key: UserPreferenceKey) => boolean;
  showSummary?: boolean;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const effectivePreferenceVisibility = useCallback(
    (key: UserPreferenceKey): boolean => {
      if (
        affectedScope === PreferenceScope.INDIVIDUAL &&
        key === UserPreferenceKey.DefaultFilters
      ) {
        return false;
      }
      if (
        key === UserPreferenceKey.PageOrder &&
        !PAGE_ORDER_SCOPES.includes(affectedScope)
      ) {
        return false;
      }
      return preferenceVisibility ? preferenceVisibility(key) : true;
    },
    [affectedScope, preferenceVisibility]
  );

  const renderTooltipContent = (
    preferences: UserPreferences
  ): React.ReactNode => (
    <PreferencesTooltipContent
      preferences={preferences}
      preferenceVisibility={effectivePreferenceVisibility}
    />
  );

  return (
    <UserPreferencesDataProvider
      affectedScope={affectedScope}
      preferencesFromFilters={preferencesFromFilters}
    >
      {({ data, status }) => {
        if (!data) return null;
        if (status !== DataProviderQueryStatus.Success) {
          return (
            <>
              <ActionBarIconButton
                icon={IconSettingsSliderThree}
                aria-label="Preferences"
                onClick={onOpen}
                tooltipLabel="Preferences"
              />
              <PreferencesModal
                isOpen={isOpen}
                onClose={onClose}
                affectedScope={affectedScope}
                preferencesFromFilters={preferencesFromFilters}
                onPreferenceUpdate={onPreferenceUpdate}
                preferenceVisibility={effectivePreferenceVisibility}
              />
            </>
          );
        }

        const visiblePreferenceKeys = getVisiblePreferenceKeys(
          effectivePreferenceVisibility
        );
        const diff = calculateVisiblePreferenceDiff(
          data.preferences,
          data.savedPreferences,
          visiblePreferenceKeys
        );
        const hasDiff = diff.length > 0;

        const renderButton = () => {
          return (
            <Box position="relative" display="inline-block">
              <ActionBarIconButton
                icon={IconSettingsSliderThree}
                aria-label="Preferences"
                onClick={onOpen}
                tooltipLabel={showSummary ? undefined : 'Preferences'}
              />
              {hasDiff && (
                <Box position="absolute" top="-2px" right="0" zIndex={1}>
                  <Badge
                    color={BadgeColor.LIGHT_BLUE}
                    size="sm"
                    data-testid="preference-diff-indicator"
                  />
                </Box>
              )}
            </Box>
          );
        };

        return (
          <>
            {showSummary ? (
              <Tooltip
                hasArrow
                placement="bottom"
                bg="#2d426a"
                color="white"
                padding="10px 14px"
                borderRadius="6px"
                maxW="360px"
                label={renderTooltipContent(data.preferences)}
                data-testid="user-preferences-tooltip"
                offset={[-90, 7]}
              >
                {renderButton()}
              </Tooltip>
            ) : (
              renderButton()
            )}
            <PreferencesModal
              isOpen={isOpen}
              onClose={onClose}
              affectedScope={affectedScope}
              preferencesFromFilters={preferencesFromFilters}
              onPreferenceUpdate={onPreferenceUpdate}
              preferenceVisibility={effectivePreferenceVisibility}
            />
          </>
        );
      }}
    </UserPreferencesDataProvider>
  );
};

export const UserPreferencesButton = ({
  showSummary = false,
}: {
  showSummary?: boolean;
}) => {
  const { activeFilterMap, filterState, dispatch } = useFilterContext();
  const basePreferenceVisibility = useCustomDashPreferenceVisibility();
  const { revelioPageId, config } = useTabContentConfig();

  const basePrimaryFilterId = config.primaryFilter?.id ?? NO_PRIMARY_FILTER_KEY;
  // For screener v2 pages, use the page ID as the consistency key to isolate their filters
  // from other pages (e.g., company screener v2 doesn't share state with company compositions)
  const isScreenerV2 = getPrimaryFilterIdForScreenerV2Page(revelioPageId);
  const pageGroupKey =
    isScreenerV2 && revelioPageId ? revelioPageId : basePrimaryFilterId;
  const isFilterGroupSynced = filterState.activeKey === pageGroupKey;

  const affectedScope = getPreferenceScope({
    pageId: revelioPageId,
    primaryFilterId: pageGroupKey,
  });

  const preferenceVisibility = useCallback(
    (key: UserPreferenceKey): boolean => {
      if (isScreenerV2Page(revelioPageId)) {
        if (
          key === UserPreferenceKey.DefaultFilters ||
          key === UserPreferenceKey.Taxonomies
        ) {
          return false;
        }
      }
      return basePreferenceVisibility(key);
    },
    [basePreferenceVisibility, revelioPageId]
  );

  const onPreferenceUpdate = useCallback(
    (
      preference: UserPreferenceKey,
      value: UserPreferences[UserPreferenceKey],
      subKey?: UserPreferenceSubKey
    ) => {
      // Guard: never apply preference-controlled filter updates to the wrong group.
      if (!isFilterGroupSynced) {
        return;
      }
      updateCustomDashFiltersForPreference(dispatch, preference, value, subKey);
    },
    [dispatch, isFilterGroupSynced]
  );

  const preferencesFromFilters = filtersToPreferences({
    activeFilterMap,
    affectedScope,
  });

  // Prevent opening the preferences UI while filter group switching is in-flight.
  // Otherwise edits can be applied to the previous group's filters/preferences.
  if (!isFilterGroupSynced) {
    return (
      <ActionBarIconButton
        icon={IconSettingsSliderThree}
        aria-label="Preferences"
        tooltipLabel="Preferences"
        isDisabled
      />
    );
  }

  return (
    <UserPreferencesButtonBase
      affectedScope={affectedScope}
      preferencesFromFilters={preferencesFromFilters}
      onPreferenceUpdate={onPreferenceUpdate}
      preferenceVisibility={preferenceVisibility}
      showSummary={showSummary}
    />
  );
};
